import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Users, BrainCircuit, Globe, Trophy, History, Loader2, ListOrdered, Lock, Settings, Palette, GraduationCap } from "lucide-react";
import { usePreferences } from "../context/PreferencesContext";
import { PIECE_THEME_ORDER, PIECE_THEMES } from "../game/pieceThemes";
import { PiecePreview } from "../components/Piece";
import { displayName } from "../lib/displayName";
import { useGameInviteNotifications } from "../hooks/useGameInviteNotifications";

export default function Lobby() {
  const { user } = useAuth();
  const { pieceTheme, setPieceTheme } = usePreferences();
  const navigate = useNavigate();
  const [loadingOnline, setLoadingOnline] = useState(false);
  useGameInviteNotifications();

  useEffect(() => {
    if (!user) return;
    const ping = () => api.post("/games/online/presence").catch(() => {});
    ping();
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, [user?.id]);

  const isAnon = !user;

  const requireAuth = () => {
    toast.info("Connexion requise pour jouer en ligne.");
    navigate("/auth");
  };

  const startOnline = async () => {
    if (isAnon) return requireAuth();
    setLoadingOnline(true);
    try {
      const { data } = await api.post("/games/online/quick");
      navigate(`/game/online/${data.room_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingOnline(false);
    }
  };

  const goOnlineRooms = () => {
    if (isAnon) return requireAuth();
    navigate("/online/rooms");
  };

  const modes = [
    {
      to: "/tutorial",
      title: "Tutoriel",
      desc: "Apprenez les bases : déplacements, captures, Ari'i…",
      icon: <GraduationCap size={28} />,
      testid: "mode-tutorial",
      onClick: null,
      locked: false,
    },
    {
      to: "/game/local",
      title: "Partie locale",
      desc: "2 joueurs sur le même appareil. Mode 'pass and play'.",
      icon: <Users size={28} />,
      testid: "mode-local",
      onClick: null,
      locked: false,
    },
    {
      to: "/game/ai",
      title: "Contre l'IA Ari'i",
      desc: "Affrontez une intelligence artificielle tactique.",
      icon: <BrainCircuit size={28} />,
      testid: "mode-ai",
      onClick: null,
      locked: false,
    },
    {
      to: null,
      title: "Match rapide",
      desc: isAnon ? "Connectez-vous pour affronter des joueurs en ligne." : "Matchmaking immédiat contre un adversaire connecté.",
      icon: <Globe size={28} />,
      testid: "mode-online",
      onClick: startOnline,
      locked: isAnon,
    },
    {
      to: null,
      title: "Salles ouvertes",
      desc: isAnon ? "Connectez-vous pour créer ou rejoindre une salle." : "Parcourez les parties en attente ou créez votre salle (publique ou privée).",
      icon: <ListOrdered size={28} />,
      testid: "mode-online-rooms",
      onClick: goOnlineRooms,
      locked: isAnon,
    },
  ];

  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-12 fade-up">
        <p className="text-xs tracking-[0.3em] text-[color:var(--primary)] mb-2" data-testid="lobby-overline">SALON</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
          {isAnon ? "Ia ora na !" : `Ia ora na, ${displayName(user)}`}
        </h1>
        <p className="text-[color:var(--text-secondary)] mb-6">Choisissez votre mode de jeu.</p>

        {isAnon && (
          <div className="glass rounded-lg p-4 mb-8 flex items-center justify-between gap-4 flex-wrap" data-testid="lobby-anon-banner">
            <div className="text-sm">
              <span className="text-[color:var(--primary)] font-semibold">Mode invité.</span>{" "}
              <span className="text-[color:var(--text-secondary)]">Connectez-vous pour jouer en ligne, sauvegarder vos stats et accéder au classement.</span>
            </div>
            <Link to="/auth" className="btn-gold px-4 py-2 rounded-md text-xs whitespace-nowrap" data-testid="lobby-anon-login">
              Se connecter
            </Link>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {modes.map((m) => {
            const inner = (
              <div className={`glass rounded-lg p-6 h-full transition cursor-pointer border border-transparent ${m.locked ? "opacity-60 hover:border-[color:var(--border)]" : "hover:-translate-y-1 hover:border-[color:var(--primary)]/50"}`}>
                <div className="w-12 h-12 rounded-md grid place-items-center bg-[color:var(--surface)] text-[color:var(--primary)] mb-4 relative">
                  {m.icon}
                  {m.locked && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[color:var(--bg-base)] border border-[color:var(--border)] grid place-items-center text-[10px]" title="Connexion requise">
                      <Lock size={10} />
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-1">{m.title}</h3>
                <p className="text-sm text-[color:var(--text-secondary)]">{m.desc}</p>
                {m.onClick && loadingOnline && !m.locked && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-[color:var(--primary)]">
                    <Loader2 className="animate-spin" size={14} /> Recherche d'un adversaire…
                  </div>
                )}
              </div>
            );
            return m.onClick ? (
              <button
                key={m.title}
                onClick={m.onClick}
                disabled={loadingOnline}
                data-testid={m.testid}
                className="text-left"
              >
                {inner}
              </button>
            ) : (
              <Link key={m.title} to={m.to} data-testid={m.testid}>
                {inner}
              </Link>
            );
          })}
        </div>

        <div className="glass rounded-lg p-5 mt-8" data-testid="lobby-piece-theme">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={16} className="text-[color:var(--primary)]" />
            <span className="font-semibold text-sm">Apparence des pions</span>
            {!isAnon && (
              <Link to="/profile" className="ml-auto text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--primary)]">
                Préférences du compte →
              </Link>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {PIECE_THEME_ORDER.map((id) => {
              const theme = PIECE_THEMES[id];
              const active = pieceTheme === id;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`lobby-theme-${id}`}
                  onClick={() => setPieceTheme(id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs transition ${
                    active
                      ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10"
                      : "border-[color:var(--border)] hover:bg-[color:var(--surface)]"
                  }`}
                >
                  <PiecePreview owner="A" themeId={id} size={22} />
                  <PiecePreview owner="B" themeId={id} size={22} />
                  <span>{theme.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-5">
          <Link to="/setup" className="glass rounded-lg p-5 flex items-center gap-4 hover:-translate-y-1 transition" data-testid="lobby-setup">
            <Settings className="text-[color:var(--primary)]" />
            <div>
              <div className="font-semibold">Configurer le plateau</div>
              <div className="text-xs text-[color:var(--text-secondary)]">Placez vous-même vos 10 pions par joueur (sauvegardé localement)</div>
            </div>
          </Link>
          <Link to="/leaderboard" className="glass rounded-lg p-5 flex items-center gap-4 hover:-translate-y-1 transition" data-testid="lobby-leaderboard">
            <Trophy className="text-[color:var(--primary)]" />
            <div>
              <div className="font-semibold">Classement mondial</div>
              <div className="text-xs text-[color:var(--text-secondary)]">Top 20 des meilleurs Ari'i</div>
            </div>
          </Link>
          {isAnon ? (
            <Link to="/auth" className="glass rounded-lg p-5 flex items-center gap-4 hover:-translate-y-1 transition" data-testid="lobby-anon-register">
              <History className="text-[color:var(--primary)]" />
              <div>
                <div className="font-semibold">Créer un compte</div>
                <div className="text-xs text-[color:var(--text-secondary)]">Sauvegardez vos parties, votre ELO et débloquez le mode en ligne</div>
              </div>
            </Link>
          ) : (
            <Link to="/profile" className="glass rounded-lg p-5 flex items-center gap-4 hover:-translate-y-1 transition" data-testid="lobby-profile">
              <History className="text-[color:var(--primary)]" />
              <div>
                <div className="font-semibold">Mon profil &amp; historique</div>
                <div className="text-xs text-[color:var(--text-secondary)]">Statistiques personnelles, ELO, dernières parties</div>
              </div>
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}
